import config from '../config'
import fetcher from './fetcher.mjs'

export type Robot = {
  name: string
  state: 'Active' | 'Stopped'
  lastRun: string
  nextRun: string | null
  latestLogs: string[]
}

export const getRobots = async (url: string): Promise<Robot[] | null> => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res?.data ?? null
  } catch (error: any) {
    return null
  }
}

export type ToBeEmailedPerson = {
  _id: string
  email: string
  name: string
  locale: string | null
  timezone: string | null
  sendInstant: string
  banned: boolean
  sending: boolean
}

export type ToBeEmailedPage = {
  data: ToBeEmailedPerson[]
  pagination: { page: number, limit: number, total: number, totalPages: number }
}

export const getToBeEmailed = async (url: string): Promise<ToBeEmailedPage | null> => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    if (!res || !Array.isArray(res.data) || !res.pagination) return null
    return { data: res.data, pagination: res.pagination }
  } catch (error: any) {
    return null
  }
}

export type AlreadyReceivedPerson = {
  _id: string
  email: string
  name: string
  locale: string | null
  timezone: string | null
  sentTime: string
  sendInstant: string | null
  banned: boolean
}

export type AlreadyReceivedPage = {
  data: AlreadyReceivedPerson[]
  pagination: { page: number, limit: number, total: number, totalPages: number }
}

export const getAlreadyReceived = async (url: string): Promise<AlreadyReceivedPage | null> => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    if (!res || !Array.isArray(res.data) || !res.pagination) return null
    return { data: res.data, pagination: res.pagination }
  } catch (error: any) {
    return null
  }
}

export const getUsers = async (url: string) => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const getAdminUserInvoices = async (url: string) => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res?.data ?? null
  } catch (error: any) {
    return null
  }
}

export const getAdminUser = async (url: string) => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res?.data ?? null
  } catch (error: any) {
    return null
  }
}

export const setAdminUserBan = async (userId: string, ban: boolean) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/admin/users/${userId}/ban`, {
    method: 'PATCH',
    body: JSON.stringify({ ban }),
  })
  return res?.data
}

export const getFreeAccessWhitelist = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/admin/free-access-whitelist`, {
      method: 'GET'
    })
    return res?.data ?? []
  } catch (error: any) {
    return []
  }
}

export const addFreeAccessEmail = async (email: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/admin/free-access-whitelist`, {
    method: 'POST',
    body: JSON.stringify({ email })
  })
  return res
}

export const removeFreeAccessEmail = async (email: string) => {
  const res = await fetcher(
    `${config.REACT_APP_API_URL}/api/admin/free-access-whitelist/${encodeURIComponent(email)}`,
    { method: 'DELETE' }
  )
  return res
}

export const getCurrentUser = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/user`, {
      method: 'GET'
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const updateUserLocale = async (locale: string) => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/user`, {
      method: 'PATCH',
      body: JSON.stringify({ locale })
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const updateUserTimezone = async (timezone: string) => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/user`, {
      method: 'PATCH',
      body: JSON.stringify({ timezone })
    })
    return res
  } catch (error: any) {
    return null
  }
}
export const getPlanLimits = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/plan-limits`, {
      method: 'GET'
    })
    return res
  } catch (error: any) {
    return null
  }
}
export const createStripeCheckoutSession = async (priceId: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/stripe/create-checkout-session`, {
    method: 'POST',
    body: JSON.stringify({ priceId })
  })
  return res
}

export const createStripeCheckoutSessionGuest = async (priceId?: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/stripe/create-checkout-session-guest`, {
    method: 'POST',
    body: JSON.stringify(priceId ? { priceId } : {})
  })
  return res
}
export const createStripePortalSession = async () => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/stripe/create-portal-session`, {
    method: 'POST'
  })
  return res
}

export const getStripePrices = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/stripe/prices`, {
      method: 'GET'
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const getClients = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/clients`, {
      method: 'GET'
    })
    return res.data
  } catch (error: any) {
    return null
  }
}
export const getClientById = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'GET'
  })
  return res.data
}

export const removeClientById = async (url: string, { arg }: { arg: { params: any } }) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}/${arg.params.id}`, {
    method: 'DELETE'
  })
  return res
}

export const getClientBalance = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'GET'
  })
  return res.data
}

export const getClientSummary = async (url: string): Promise<InvoicesSummary | null> => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res.data ?? null
  } catch (error: any) {
    return null
  }
}

export const getInvoices = async (url: string) => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res.data
  } catch (error: any) {
    return null
  }
}

export type InvoicesSummary = {
  totalAmount: number
  invoiceCount: number
  paidAmount: number
  paidCount: number
  dueAmount: number
  dueCount: number
}

export const getInvoicesSummary = async (url: string): Promise<InvoicesSummary | null> => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'GET'
    })
    return res.data ?? null
  } catch (error: any) {
    return null
  }
}

export const getCurrencies = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/invoices/currencies`, {
      method: 'GET'
    })
    return res.data
  } catch (error: any) {
    return null
  }
}

export const getDefaultCurrency = async (url = '/api/invoices/default-currency') => {
  try {
    const path = url.startsWith('http') ? url : `${config.REACT_APP_API_URL}${url}`
    const res = await fetcher(path, {
      method: 'GET',
    })
    return res.data
  } catch (error: any) {
    return null
  }
}

export const getInvoiceById = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'GET'
  })
  return res.data
}

export const logout = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/auth/logout`, {
      method: 'GET'
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const updateBusinessDetails = async (url: string, { arg }: { arg: { body: any } }) => {
  const formattedBody: any = {}
  Object.keys(arg.body).forEach((key) => {
    let formattedKey = key.replace('businessDetails.', '')
    if(formattedKey.startsWith('tax.')) {
      formattedKey = formattedKey.replace('tax.', '')
      formattedBody.tax = { ...formattedBody.tax, [formattedKey]: arg.body[key] }
    } else if (formattedKey.startsWith('currency.')) {
      formattedKey = formattedKey.replace('currency.', '')
      formattedBody.currency = { ...formattedBody.currency, [formattedKey]: arg.body[key] }
    } else {
      formattedBody[formattedKey] = arg.body[key]
    }
  })
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
      method: 'PATCH',
      body: JSON.stringify(formattedBody)
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const saveOrUpdateClient = async (url: string, { arg }: { arg: { body: any } }) => {
  const formattedBody: any = {}
  Object.keys(arg.body).forEach((key) => {
    const formattedKey = key.replace('client.', '')
    formattedBody[formattedKey] = arg.body[key]
  })
  let method = ''
  if(formattedBody._id) {
    method = 'PATCH'
  } else {
    method = 'POST'
    delete formattedBody._id
  }
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method,
    body: JSON.stringify(formattedBody)
  })
  return res
}

export const checkTradingNameExists = async (tradingName: string, excludeId?: string) => {
  const params = new URLSearchParams()
  params.set('name', tradingName)
  if (excludeId) params.set('excludeId', excludeId)
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/clients/trading-name-exists?${params.toString()}`, {
      method: 'GET',
    })
    return !!res?.data?.exists
  } catch (error: any) {
    return false
  }
}

export const saveOrUpdateInvoice = async (url: string, { arg }: { arg: { body: any } }) => {
  let method = ''
  if(arg.body._id) {
    method = 'PATCH'
  } else {
    method = 'POST'
  }

  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method,
    body: JSON.stringify(arg.body)
  })
  return res
}

export const updateUnsentInvoicesTax = async (
  url: string,
  { arg }: { arg: { body: { enableTax: boolean, tax?: { name?: string, percentage?: string | number } } } }
) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'PATCH',
    body: JSON.stringify(arg.body),
  })
  return res
}

export const getClonedInvoice = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'GET'
  })
  return res
}

export const removeInvoice = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'DELETE',
  })
  return res
}

export const sendInvoiceEmail = async (url: string, { arg }: { arg: { body: { invoiceId: string, content: string, ccEmail?: string } } }) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'POST',
    body: JSON.stringify(arg.body)
  })
  return res
}

export const markInvoiceSentManually = async (
  _url: string,
  { arg }: { arg: { invoiceId: string } },
) => {
  const res = await fetcher(
    `${config.REACT_APP_API_URL}/api/invoices/${arg.invoiceId}/mark-sent-manually`,
    { method: 'POST' },
  )
  return res
}

export const unmarkInvoiceSentManually = async (
  _url: string,
  { arg }: { arg: { invoiceId: string } },
) => {
  const res = await fetcher(
    `${config.REACT_APP_API_URL}/api/invoices/${arg.invoiceId}/mark-sent-manually`,
    { method: 'DELETE' },
  )
  return res
}
export const getEmailTemplate = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'GET',
  })
  return res
}

export const getNextCode = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/invoices/next-code`, {
      method: 'GET'
    })
    return res
  } catch (error: any) {
    return null
  }
}

export const getInvoiceHistory = async (url: string) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'GET'
  })
  return res
}

export const getOverdueReminderEmailSettings = async () => {
  try {
    const res = await fetcher(`${config.REACT_APP_API_URL}/api/user/overdue-reminder-email`, {
      method: 'GET',
    })
    return res.data
  } catch (error: any) {
    return null
  }
}

export const updateReminders = async (_url: string, { arg }: { arg: { body: any } }) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/user/reminders`, {
    method: 'PATCH',
    body: JSON.stringify(arg.body),
  })
  return res
}

export const submitFeedback = async (
  _url: string,
  { arg }: { arg: { body: { type?: string; message: string } } },
) => {
  const res = await fetcher(`${config.REACT_APP_API_URL}/api/user/feedback`, {
    method: 'POST',
    body: JSON.stringify(arg.body),
  })
  return res
}

export const playgroundCall = async (_: string, { arg }: { arg: { apiPath: string } }) => {
  if(arg.apiPath !== '') {
    const res = await fetcher(`${config.REACT_APP_API_URL}${arg.apiPath}`, {
      method: 'GET',
    })
    return res
  }
}
